/*
 * Shared-layout SECTIONS (pure structural transform, no DOM).
 *
 * Ported from the approved prototype gm/v2-layout-sections.js (366 lines), lines 35-362 (all of it: tokenizer,
 * structure, known shapes, analyze, apply, removeSection, restoreMode, restoreSection, addSection, removeManaged,
 * flags, flagsEqual). Same algorithms, strings and return shapes.
 *
 * Finds the complete table ROWS that make up a shared layout's header and footer, so one message can leave them out
 * of its composed email, and performs the layout's own structural edits (remove / restore a whole section, add or
 * remove a managed full-width row). It never edits anything else: every result is one exact splice, re-analysed.
 * Anything it cannot split with certainty is UNSUPPORTED (ok:false with a reason); there is never a partial omission.
 *
 * Deviation (recorded): the managed-row id generator is injectable (addSection's 4th argument, newRowId's 2nd), with
 * the prototype's Date.now-based default, so tests can be deterministic.
 */

export type Span = [number, number];
export type Side = "header" | "footer";
interface Fail { ok: false; reason: string }

/* Module-level global regex: every use resets lastIndex exactly where the prototype did. */
const SLOT_RE = /\{\{-?\s*body\s*-?\}\}/g;
const TABLEISH: Record<string, 1> = { table: 1, tbody: 1, thead: 1, tfoot: 1, tr: 1, td: 1, th: 1 };
const RAW: Record<string, 1> = { style: 1, script: 1, title: 1, textarea: 1 };
const LIQ_BLOCKS = ["if", "unless", "case", "for", "capture", "raw", "comment", "tablerow"];

function fail(reason: string): Fail { return { ok: false, reason }; }

/* ---- tokenizer ---- */
interface Tok { t: "text" | "comment" | "open" | "close" | "doctype" | "raw"; s: number; e: number; name?: string; attrs?: string; cond?: boolean; self?: boolean }
function tokenize(src: string): Tok[] | Fail {
  const out: Tok[] = []; let i = 0; const n = src.length;
  while (i < n) {
    const lt = src.indexOf("<", i);
    if (lt < 0) { out.push({ t: "text", s: i, e: n }); break; }
    if (lt > i) out.push({ t: "text", s: i, e: lt });
    if (src.startsWith("<!--", lt)) {
      const ce = src.indexOf("-->", lt + 4); if (ce < 0) return fail("a comment is not closed");
      out.push({ t: "comment", s: lt, e: ce + 3, cond: /^<!--\s*\[if\b/i.test(src.slice(lt, lt + 12)) || /<!\[endif\]/i.test(src.slice(lt, ce)) });
      i = ce + 3; continue;
    }
    if (src[lt + 1] === "!") { const de = src.indexOf(">", lt); if (de < 0) return fail("a declaration is not closed"); out.push({ t: "doctype", s: lt, e: de + 1 }); i = de + 1; continue; }
    const close = src[lt + 1] === "/";
    const m = /^[a-zA-Z][a-zA-Z0-9-]*/.exec(src.slice(lt + (close ? 2 : 1), lt + (close ? 2 : 1) + 40));
    if (!m) { out.push({ t: "text", s: lt, e: lt + 1 }); i = lt + 1; continue; }
    const name = m[0].toLowerCase(); let j = lt + (close ? 2 : 1) + m[0].length; let q: string | null = null;
    for (; j < n; j++) { const ch = src[j]; if (q) { if (ch === q) q = null; } else if (ch === "\"" || ch === "'") q = ch; else if (ch === ">") break; }
    if (j >= n) return fail("a tag is not closed");
    const tok: Tok = { t: close ? "close" : "open", s: lt, e: j + 1, name, attrs: close ? "" : src.slice(lt + 1 + m[0].length, j), self: !close && src[j - 1] === "/" };
    out.push(tok); i = j + 1;
    if (!close && RAW[name]) {
      const re = new RegExp("</" + name + "\\s*>", "ig"); re.lastIndex = i; const rm = re.exec(src);
      if (!rm) return fail("a <" + name + "> element is not closed");
      if (rm.index > i) out.push({ t: "raw", s: i, e: rm.index });
      out.push({ t: "close", s: rm.index, e: rm.index + rm[0].length, name }); i = rm.index + rm[0].length;
    }
  }
  return out;
}
function attr(attrs: string | null | undefined, nm: string): string | null {
  const m = new RegExp("(?:^|\\s)" + nm + "\\s*=\\s*(\"([^\"]*)\"|'([^']*)'|([^\\s\"'>]+))", "i").exec(attrs || "");
  return m ? ((m[2] != null ? m[2] : m[3] != null ? m[3] : m[4]) as string) : null;
}
function classes(attrs: string): string[] { return String(attr(attrs, "class") || "").split(/\s+/).filter(Boolean); }

/* Liquid block tags opened and closed within a span, in order. */
function liquidBalanced(text: string): boolean {
  const re = /\{%-?\s*(end)?(\w+)/g; let m: RegExpExecArray | null; const stack: string[] = [];
  while ((m = re.exec(text))) {
    const w = m[2] as string; if (LIQ_BLOCKS.indexOf(w) < 0) continue;
    if (!m[1]) stack.push(w); else if (stack.pop() !== w) return false;
  }
  return stack.length === 0;
}

/* ---- structure around the single slot ---- */
export interface LayoutEl { name: string; s: number; openE: number; e: number; attrs: string; parent: LayoutEl | null; kids: LayoutEl[]; gaps: Tok[]; regions: string[] }
export interface LayoutStructure { ok: true; src: string; rows: LayoutEl[]; bodyIndex: number; labels: (string | null)[]; labelStart: number[]; slotCell: LayoutEl }
export function structure(input: unknown): LayoutStructure | Fail {
  const src = String(input || "");
  SLOT_RE.lastIndex = 0; const slots: number[] = []; let sm: RegExpExecArray | null; while ((sm = SLOT_RE.exec(src))) slots.push(sm.index);
  if (slots.length !== 1) return fail(slots.length ? "the layout has " + slots.length + " {{ body }} slots" : "the layout has no {{ body }} slot");
  const slot0 = slots[0] as number;
  const toks = tokenize(src); if (!Array.isArray(toks)) return toks;
  const els: LayoutEl[] = [], stack: number[] = []; let slotEl: LayoutEl | null = null, slotSeen = false;
  const top = (): LayoutEl | null => (stack.length ? (els[stack[stack.length - 1] as number] as LayoutEl) : null);
  for (let x = 0; x < toks.length; x++) {
    const t = toks[x] as Tok;
    if (t.t === "open" && TABLEISH[t.name as string] && !t.self) {
      const el: LayoutEl = { name: t.name as string, s: t.s, openE: t.e, e: -1, attrs: t.attrs as string, parent: top(), kids: [], gaps: [], regions: [] };
      if (el.parent) el.parent.kids.push(el);
      els.push(el); stack.push(els.length - 1); continue;
    }
    if (t.t === "close" && TABLEISH[t.name as string]) {
      const tp = top(); if (!tp || tp.name !== t.name) return fail("a </" + t.name + "> does not close the element it should (implicit or misnested table markup)");
      tp.e = t.e; stack.pop(); continue;
    }
    const cur = top();
    if (t.t === "comment") {
      const body = src.slice(t.s + 4, t.e - 3); const rg = /^\s*region:([\w-]+)\s*$/.exec(body);
      if (rg) for (let k = stack.length - 1; k >= 0; k--) { const sk = els[stack[k] as number] as LayoutEl; if (sk.name === "tr") { sk.regions.push(rg[1] as string); break; } }
    }
    if (cur && (cur.name === "table" || cur.name === "tbody" || cur.name === "thead" || cur.name === "tfoot" || cur.name === "tr")) cur.gaps.push(t);
    if (t.t === "text" && slot0 >= t.s && slot0 < t.e) {
      slotSeen = true;
      if (!cur || (cur.name !== "td" && cur.name !== "th")) return fail("{{ body }} is not inside a table cell");
      slotEl = cur;
    }
  }
  if (stack.length) return fail("a <" + (top() as LayoutEl).name + "> is not closed");
  if (!slotSeen || !slotEl) return fail("{{ body }} is inside a tag, comment or style block");
  const row = slotEl.parent; if (!row || row.name !== "tr") return fail("the {{ body }} cell is not in a table row");
  const box = row.parent; if (!box) return fail("the {{ body }} row is not in a table");
  const rows = box.kids;
  if (rows.some((r) => r.name !== "tr")) return fail("the body table mixes rows with other table parts");
  /* between rows: whitespace and plain comments only; label = the plain comment directly before a row */
  const labels: (string | null)[] = rows.map(() => null); const labelStart: number[] = rows.map(() => -1);
  for (let g = 0; g < box.gaps.length; g++) {
    const gt = box.gaps[g] as Tok;
    if (gt.t === "text") { if (/\S/.test(src.slice(gt.s, gt.e))) return fail("there is text or Liquid between the layout's rows"); continue; }
    if (gt.t === "comment" && !gt.cond) continue;
    return fail("there is other markup between the layout's rows");
  }
  rows.forEach((r, ri) => {
    const prevEnd = ri ? (rows[ri - 1] as LayoutEl).e : box.openE;
    const between = box.gaps.filter((gt) => gt.s >= prevEnd && gt.e <= r.s && gt.t === "comment");
    const last = between[between.length - 1];
    if (last && !/\S/.test(src.slice(last.e, r.s))) { labels[ri] = src.slice(last.s + 4, last.e - 3).trim().toUpperCase(); labelStart[ri] = last.s; }
  });
  for (let ri2 = 0; ri2 < rows.length; ri2++) {
    const r2 = rows[ri2] as LayoutEl;
    if (r2.kids.some((c) => c.name !== "td" && c.name !== "th")) return fail("a layout row holds something other than cells");
    for (let gi = 0; gi < r2.gaps.length; gi++) { const rgt = r2.gaps[gi] as Tok; if (rgt.t === "text" && !/\S/.test(src.slice(rgt.s, rgt.e))) continue; if (rgt.t === "comment" && !rgt.cond) continue; return fail("a layout row has markup outside its cells"); }
  }
  return { ok: true, src, rows, bodyIndex: rows.indexOf(row), labels, labelStart, slotCell: slotEl };
}

function cell(r: LayoutEl): LayoutEl | null { return r.kids.length === 1 ? (r.kids[0] as LayoutEl) : null; }
function sameList(a: (string | null)[], b: (string | null)[]): boolean { return a.length === b.length && a.every((v, i) => v === b[i]); }

/* MANAGED rows: full-width card sections added from Visual. A row is managed when its single cell carries
   data-layout-section="header|footer" AND a stable data-layout-row="<id>". That marker is the ownership record (no
   hidden copy anywhere). Managed header rows sit between the known header rows and the body row; managed footer
   rows between the body row and the known footer rows. */
const ROW_ID = /^[A-Za-z][\w-]{0,39}$/;
function countRe(text: string, re: RegExp): number { return (text.match(re) || []).length; }
type ManagedState = { ok: false } | { ok: true; side: Side; id: string };
/* null: the row doesn't claim to be managed. { ok:false }: it claims to (data-layout-row) but its ownership can't be
   proven, which makes the whole layout unsupported. { ok:true, side, id }: a managed row. */
function managedState(S: LayoutStructure, i: number): ManagedState | null {
  const r = S.rows[i] as LayoutEl, c = cell(r); const claimed = r.kids.some((k) => attr(k.attrs, "data-layout-row") != null);
  if (!claimed) return null; if (!c) return { ok: false };
  const side = attr(c.attrs, "data-layout-section"), id = attr(c.attrs, "data-layout-row");
  if (!(side === "header" || side === "footer") || !id || !ROW_ID.test(id)) return { ok: false };
  const esc = id.replace(/[-]/g, "\\-"); const open = new RegExp("<!--\\s*region:" + esc + "\\s*-->", "g"), close = new RegExp("<!--\\s*\\/region:" + esc + "\\s*-->", "g");
  const inner = S.src.slice(c.openE, c.e);
  if (countRe(S.src, open) !== 1 || countRe(S.src, close) !== 1 || countRe(inner, open) !== 1 || countRe(inner, close) !== 1 || !sameList(r.regions, [id])) return { ok: false };
  if (inner.search(open) > inner.search(close)) return { ok: false };
  return { ok: true, side, id };
}
function managedOf(S: LayoutStructure, i: number): { ok: true; side: Side; id: string } | null { const m = managedState(S, i); return m && m.ok ? m : null; }
function badManaged(S: LayoutStructure): boolean { for (let i = 0; i < S.rows.length; i++) { const m = managedState(S, i); if (m && !m.ok) return true; } return false; }
interface RowSpec { cls?: string; label?: string | null; regions: string[] }
/* A known shape's default header and footer rows are each a fixed group that is either all present or all absent
   (a whole section may have been removed); a group is never accepted partially. */
function group(S: LayoutStructure, from: number, to: number, spec: RowSpec[]): boolean {
  if (to - from !== spec.length) return false;
  for (let i = 0; i < spec.length; i++) {
    const r = S.rows[from + i] as LayoutEl, sp = spec[i] as RowSpec; const c = cell(r);
    if (!c || managedState(S, from + i) || !sameList(r.regions, sp.regions) || (sp.label !== undefined && S.labels[from + i] !== sp.label) || (sp.cls && classes(c.attrs).indexOf(sp.cls) < 0)) return false;
  }
  return true;
}
const range = (x: number, y: number): number[] => { const o: number[] = []; for (let i = x; i < y; i++) o.push(i); return o; };
interface ShapeHit { header: number[]; footer: number[]; knownHeader: number[]; knownFooter: number[] }
function knownShape(S: LayoutStructure, bodySpec: RowSpec, headSpec: RowSpec[], footSpec: RowSpec[]): ShapeHit | null {
  const b = S.bodyIndex, n = S.rows.length; let m: ReturnType<typeof managedOf>;
  if (badManaged(S) || !group(S, b, b + 1, [bodySpec])) return null;
  let hm = b; while (hm > 0 && (m = managedOf(S, hm - 1)) && m.side === "header") hm--;   /* managed header rows: hm..b-1 */
  let fm = b + 1; while (fm < n && (m = managedOf(S, fm)) && m.side === "footer") fm++;  /* managed footer rows: b+1..fm-1 */
  if (!(hm === 0 || group(S, 0, hm, headSpec)) || !(fm === n || group(S, fm, n, footSpec))) return null;
  return { header: range(0, b), footer: range(b + 1, n), knownHeader: range(0, hm), knownFooter: range(fm, n) };
}
interface Shape { id: string; name: string; header: string; footer: string; match: (S: LayoutStructure) => ShapeHit | null }
const SHAPE_LIST: Shape[] = [
  { id: "standard", name: "Standard structure",
    header: "The header row (logo and tagline).", footer: "The footer row.",
    match: (S) => knownShape(S, { cls: "content", regions: [] }, [{ cls: "header", regions: ["header"] }], [{ cls: "footer", regions: ["footer"] }]) },
  { id: "golden1", name: "Golden 1 operational structure",
    header: "The logo and hero rows.", footer: "The banner, disclosures, footer and copyright rows.",
    match: (S) => knownShape(S, { cls: "g1-body", label: "MESSAGE BODY", regions: [] },
      [{ label: "LOGO", regions: [] }, { label: "HERO", regions: ["header"] }],
      [{ label: "BANNER", regions: [] }, { label: null, regions: [] }, { label: "DISCLOSURES", regions: ["disclosures"] }, { label: "FOOTER", regions: ["footer"] }, { label: "COPYRIGHT", regions: ["copyright"] }]) },
  /* marked: every non-body row's single cell is marked header (above) or footer (below). With no such row left the
     body cell itself must carry data-layout-section="body" (a stable anchor), so a fully reduced marked layout stays known. */
  { id: "marked", name: "Marked sections",
    header: "The rows marked as header.", footer: "The rows marked as footer.",
    match: (S) => {
      if (badManaged(S)) return null;
      const h: number[] = [], f: number[] = [];
      for (let i = 0; i < S.rows.length; i++) {
        if (i === S.bodyIndex) continue;
        const c = cell(S.rows[i] as LayoutEl); const mk = c && attr(c.attrs, "data-layout-section");
        if (mk === "header" && i < S.bodyIndex) h.push(i); else if (mk === "footer" && i > S.bodyIndex) f.push(i); else return null;
      }
      const bc = cell(S.rows[S.bodyIndex] as LayoutEl); const anchored = !!bc && attr(bc.attrs, "data-layout-section") === "body";
      if (!h.length && !f.length && !anchored) return null;
      const plain = (list: number[]): number[] => list.filter((i) => !managedOf(S, i));
      return { header: h, footer: f, knownHeader: plain(h), knownFooter: plain(f) };
    } },
];

/* whole lines when only whitespace sits around the span */
function widen(src: string, s: number, e: number): Span {
  let a = s; while (a > 0 && (src[a - 1] === " " || src[a - 1] === "\t")) a--;
  let b = e; while (b < src.length && (src[b] === " " || src[b] === "\t")) b++;
  const lineL = a === 0 || src[a - 1] === "\n"; const lineR = b === src.length || src[b] === "\n" || src[b] === "\r";
  if (lineL && lineR) { if (src[b] === "\r") b++; if (src[b] === "\n") b++; return [a, b]; }
  return [s, e];
}
function spanOf(S: LayoutStructure, idx: number[]): Span | null {
  if (!idx.length) return null;
  const first = idx[0] as number, last = idx[idx.length - 1] as number;
  const s = (S.labelStart[first] as number) >= 0 ? (S.labelStart[first] as number) : (S.rows[first] as LayoutEl).s; const e = (S.rows[last] as LayoutEl).e;
  return widen(S.src, s, e);
}

export interface SectionPart { span: Span; rows: number[]; text: string }
export interface SectionPartError { error: string }
export interface ManagedRow { id: string; side: Side; row: number; span: Span; text: string }
export interface LayoutAnalysisOk {
  ok: true;
  src: string;
  shape: string;
  name: string;
  header: SectionPart | null;
  footer: SectionPart | null;
  /* the prototype stores a {error} here too when only the default rows' span fails the Liquid/slot check; kept as is */
  knownHeader: SectionPart | SectionPartError | null;
  knownFooter: SectionPart | SectionPartError | null;
  managed: ManagedRow[];
  headerText: string;
  footerText: string;
  body: { span: Span };
}
export type LayoutAnalysis = LayoutAnalysisOk | Fail;

let cache: { src: string | null; res: LayoutAnalysis | null } = { src: null, res: null };
/* { ok, shape, name, header:{span,rows,text}|null, footer:..., reason } */
export function analyze(input: unknown): LayoutAnalysis {
  const src = String(input || "");
  if (cache.src === src) return cache.res as LayoutAnalysis;
  const S = structure(src); let res: LayoutAnalysis;
  if (!S.ok) res = { ok: false, reason: S.reason };
  else {
    let hit: ShapeHit | null = null, shape: Shape | null = null;
    for (let i = 0; i < SHAPE_LIST.length && !hit; i++) { hit = (SHAPE_LIST[i] as Shape).match(S); if (hit) shape = SHAPE_LIST[i] as Shape; }
    if (!hit || !shape) res = { ok: false, reason: "its rows don't match a structure this preview can split safely" };
    else {
      const part = (idx: number[], label: string): SectionPart | SectionPartError | null => {
        const sp = spanOf(S, idx); if (!sp) return null;
        const text = src.slice(sp[0], sp[1]);
        SLOT_RE.lastIndex = 0; if (SLOT_RE.test(text)) return { error: "the " + label + " rows contain {{ body }}" };
        if (!liquidBalanced(text)) return { error: "the " + label + " rows open or close Liquid logic that continues outside them" };
        return { span: sp, rows: idx.slice(), text };
      };
      const h = part(hit.header, "header"), f = part(hit.footer, "footer");
      const kh = part(hit.knownHeader || [], "header"), kf = part(hit.knownFooter || [], "footer");
      const managed: ManagedRow[] = [], seenId: Record<string, 1> = {}; const dup: { id: string | null } = { id: null };
      hit.header.concat(hit.footer).forEach((i) => {
        const mo = managedOf(S, i); if (!mo) return; if (seenId[mo.id]) dup.id = mo.id; seenId[mo.id] = 1;
        const row = S.rows[i] as LayoutEl; const sp = widen(src, row.s, row.e); managed.push({ id: mo.id, side: mo.side, row: i, span: sp, text: src.slice(sp[0], sp[1]) });
      });
      const dupId = dup.id;
      if (h && "error" in h) res = { ok: false, reason: h.error };
      else if (f && "error" in f) res = { ok: false, reason: f.error };
      else if (dupId) res = { ok: false, reason: "two added sections share the id " + dupId };
      else res = { ok: true, src, shape: shape.id, name: shape.name, header: h, footer: f, knownHeader: kh, knownFooter: kf, managed, headerText: shape.header, footerText: shape.footer, body: { span: spanOf(S, [S.bodyIndex]) as Span } };
    }
  }
  cache = { src, res };
  return res;
}

export interface SectionFlagsInput { showLayoutHeader?: unknown; showLayoutFooter?: unknown }
export function flags(snap: SectionFlagsInput | null | undefined): { header: boolean; footer: boolean } { return { header: !snap || snap.showLayoutHeader !== false, footer: !snap || snap.showLayoutFooter !== false }; }
export function flagsEqual(a: SectionFlagsInput | null | undefined, b: SectionFlagsInput | null | undefined): boolean { const x = flags(a), y = flags(b); return x.header === y.header && x.footer === y.footer; }

export interface ApplyResult { ok: boolean; html: string; changed: boolean; hidden: { header: boolean; footer: boolean }; reason?: string; shape?: string }
/* show: { header, footer } (true = keep). Returns { ok, html, changed, hidden: { header, footer }, reason }.
   Both shown: the source unchanged, nothing analysed. Otherwise all requested sections are removed, or none. */
export function apply(input: unknown, showIn?: { header?: unknown; footer?: unknown } | null): ApplyResult {
  const src = String(input || ""); const show = showIn || {};
  const wantH = show.header === false, wantF = show.footer === false;
  if (!wantH && !wantF) return { ok: true, html: src, changed: false, hidden: { header: false, footer: false } };
  const A = analyze(src);
  if (!A.ok) return { ok: false, html: src, changed: false, hidden: { header: false, footer: false }, reason: A.reason };
  /* a section the layout no longer has is already left out: hiding it again is a no-op (idempotent) */
  const cuts: Span[] = []; if (wantH && A.header) cuts.push(A.header.span); if (wantF && A.footer) cuts.push(A.footer.span);
  if (!cuts.length) return { ok: true, html: src, changed: false, hidden: { header: false, footer: false }, shape: A.shape };
  cuts.sort((a, b) => a[0] - b[0]);
  let html = "", at = 0; cuts.forEach((c) => { html += src.slice(at, c[0]); at = c[1]; }); html += src.slice(at);
  SLOT_RE.lastIndex = 0; const n = (html.match(SLOT_RE) || []).length;
  if (n !== 1) return { ok: false, html: src, changed: false, hidden: { header: false, footer: false }, reason: "removing the rows would not leave exactly one {{ body }}" };
  return { ok: true, html, changed: true, hidden: { header: wantH && !!A.header, footer: wantF && !!A.footer }, shape: A.shape };
}

/* ---- shared-layout structural edits (the layout's own full source; the caller saves it as a draft) ---- */
export type RemoveSectionResult = { ok: true; src: string; removed: string; span: Span; shape: string } | Fail;
/* Remove one complete section (all its owned rows). Refuses when the source isn't a recognised shape or the section is absent. */
export function removeSection(input: unknown, which: string): RemoveSectionResult {
  const src = String(input || ""); if (which !== "header" && which !== "footer") return { ok: false, reason: "unknown section" };
  const A = analyze(src); if (!A.ok) return { ok: false, reason: A.reason };
  const sec = A[which]; if (!sec) return { ok: false, reason: "this layout has no " + which + " to remove" };
  const r = apply(src, which === "header" ? { header: false } : { footer: false });
  if (!r.ok || !r.changed) return { ok: false, reason: r.reason || "the " + which + " could not be removed" };
  return { ok: true, src: r.html, removed: sec.text, span: sec.span, shape: A.shape };
}

export type RestoreModeResult = { ok: true; mode: "whole" | "defaults" } | Fail;
const knownKey = (which: Side): "knownHeader" | "knownFooter" => (which === "header" ? "knownHeader" : "knownFooter");
/* What restoring this side from version F would do: "whole" (side entirely absent: all of F's side, its added sections
   included, refused if any of their IDs is already used in the current source), "defaults" (the side keeps added
   sections but lost its default rows: only F's default rows), or a refusal. */
function restoreModeOf(A: LayoutAnalysisOk, F: LayoutAnalysisOk, which: Side): RestoreModeResult {
  const K = knownKey(which);
  if (!A[which]) {
    if (!F[which]) return { ok: false, reason: "that version has no " + which + " that can be restored" };
    const clash = F.managed.filter((m) => m.side === which && (A.src.indexOf('"' + m.id + '"') >= 0 || new RegExp("region:" + m.id.replace(/[-]/g, "\\-") + "\\b").test(A.src)))[0];
    if (clash) return { ok: false, reason: "that version's added section " + clash.id + " uses an ID that is already in this layout" };
    return { ok: true, mode: "whole" };
  }
  if (!A[K]) { if (!F[K]) return { ok: false, reason: "that version has no default " + which + " rows to restore" }; return { ok: true, mode: "defaults" }; }
  return { ok: false, reason: "this layout already has its " + which };
}
/* Public form: { ok:false } (no reason) when either source can't be read or the shapes differ, as the prototype's API. */
export function restoreMode(cur: unknown, from: unknown, which: Side): RestoreModeResult | { ok: false } {
  const A = analyze(cur), F = analyze(from); if (!A.ok || !F.ok || A.shape !== F.shape) return { ok: false };
  return restoreModeOf(A, F, which);
}

export type RestoreSectionResult = { ok: true; src: string; span: Span; shape: string; mode: "whole" | "defaults" } | Fail;
/* Restore one section from another (saved) source of the SAME shape: its complete owned rows are spliced in at the
   body row's boundary (header directly above the body row and its label, footer directly below it). Every other byte
   of the current source stays as it is; the result must re-analyse as the same shape with that section present and
   byte-identical to the one taken from the version. */
export function restoreSection(curIn: unknown, which: string, fromIn: unknown): RestoreSectionResult {
  const cur = String(curIn || ""); const from = String(fromIn || "");
  if (which !== "header" && which !== "footer") return { ok: false, reason: "unknown section" };
  const A = analyze(cur); if (!A.ok) return { ok: false, reason: "the current layout can't be split safely (" + A.reason + ")" };
  const K = knownKey(which);
  const F = analyze(from); if (!F.ok) return { ok: false, reason: "that version can't be read safely" };
  if (F.shape !== A.shape) return { ok: false, reason: "that version has a different structure, so its " + which + " can't be placed here" };
  const mode = restoreModeOf(A, F, which); if (!mode.ok) return mode;
  /* whole: the side is entirely absent, so the version's WHOLE side (default rows and its added sections) comes back exactly.
     defaults: the side keeps its own added sections, so only the version's default rows come back, at the outer edge. */
  const take = (mode.mode === "whole" ? F[which] : F[K]) as SectionPart;
  const at = mode.mode === "whole" ? (which === "header" ? A.body.span[0] : A.body.span[1]) : (which === "header" ? (A.header as SectionPart).span[0] : (A.footer as SectionPart).span[1]);
  const text = take.text; const next = cur.slice(0, at) + text + cur.slice(at);
  const B = analyze(next); const got = (B.ok ? (mode.mode === "whole" ? B[which] : B[K]) : null) as SectionPart | null;
  const addedNow = mode.mode === "whole" ? F.managed.filter((m) => m.side === which).length : 0;
  if (!B.ok || B.shape !== A.shape || !got || got.text !== text || B.managed.length !== A.managed.length + addedNow) return { ok: false, reason: "the restored " + which + " would not fit this layout's structure" };
  if (next.slice(0, got.span[0]) + next.slice(got.span[1]) !== cur) return { ok: false, reason: "restoring would change other parts of the layout" };
  SLOT_RE.lastIndex = 0; if ((next.match(SLOT_RE) || []).length !== 1) return { ok: false, reason: "restoring would not leave exactly one {{ body }}" };
  return { ok: true, src: next, span: got.span, shape: A.shape, mode: mode.mode };
}

/* Add one managed full-width section row: header -> directly above the body row (below the other header rows),
   footer -> directly below it. One <tr><td> spanning the card, with its own region for nested blocks. */
export function newRowId(src: string, now: () => number = Date.now): string {
  let n = 0, id: string;
  do { id = "sec-" + (now() + n).toString(36).slice(-6); n++; } while (src.indexOf('"' + id + '"') >= 0 || src.indexOf("region:" + id) >= 0);
  return id;
}
function rowMarkup(which: Side, id: string): string {
  return ["<tr>", '<td data-layout-section="' + which + '" data-layout-row="' + id + '" style="padding: 24px 32px; background-color: #EEF0F3; text-align: left;">',
    "<!-- region:" + id + " -->", '<h2 data-edit="heading" style="margin: 0; font-size: 20px; line-height: 1.3;">Section heading</h2>', "<!-- /region:" + id + " -->", "</td>", "</tr>", ""].join("\n");
}
export type AddSectionResult = { ok: true; src: string; id: string; span: Span; shape: string } | Fail;
export function addSection(input: unknown, which: string, idIn?: string | null, makeId: (src: string) => string = newRowId): AddSectionResult {
  const src = String(input || ""); if (which !== "header" && which !== "footer") return { ok: false, reason: "unknown section" };
  const A = analyze(src); if (!A.ok) return { ok: false, reason: A.reason };
  const id = idIn || makeId(src); if (!ROW_ID.test(id) || src.indexOf('"' + id + '"') >= 0 || src.indexOf("region:" + id) >= 0) return { ok: false, reason: "that section id is already used" };
  const at = which === "header" ? A.body.span[0] : A.body.span[1]; const text = rowMarkup(which, id);
  const next = src.slice(0, at) + text + src.slice(at); const B = analyze(next);
  const mine = B.ok ? B.managed.filter((m) => m.id === id && m.side === which)[0] : null;
  if (!B.ok || B.shape !== A.shape || !mine || mine.text !== text) return { ok: false, reason: "a section can't be added safely to this layout's structure" };
  if (next.slice(0, mine.span[0]) + next.slice(mine.span[1]) !== src) return { ok: false, reason: "adding would change other parts of the layout" };
  SLOT_RE.lastIndex = 0; if ((next.match(SLOT_RE) || []).length !== 1) return { ok: false, reason: "adding would not leave exactly one {{ body }}" };
  return { ok: true, src: next, id, span: mine.span, shape: A.shape };
}
export type RemoveManagedResult = { ok: true; src: string; removed: string; side: Side } | Fail;
/* Remove one managed row (its whole <tr>, including everything nested in it). */
export function removeManaged(input: unknown, id: string): RemoveManagedResult {
  const src = String(input || ""); const A = analyze(src); if (!A.ok) return { ok: false, reason: A.reason };
  const m = A.managed.filter((x) => x.id === id)[0]; if (!m) return { ok: false, reason: "that section is no longer in this layout" };
  const next = src.slice(0, m.span[0]) + src.slice(m.span[1]); const B = analyze(next);
  if (!B.ok || B.shape !== A.shape || B.managed.length !== A.managed.length - 1) return { ok: false, reason: "removing it would break this layout's structure" };
  SLOT_RE.lastIndex = 0; if ((next.match(SLOT_RE) || []).length !== 1) return { ok: false, reason: "removing would not leave exactly one {{ body }}" };
  return { ok: true, src: next, removed: m.text, side: m.side };
}

/* The recognised structure ids, in match order. */
export const SHAPES: string[] = SHAPE_LIST.map((s) => s.id);
